// TwinProvider — single source of truth for `/api/twin/current`.
//
// Polls every 60 s while the tab is focused and re-fetches on `focus`.
// Exposes:
//   • twin                — the full battle payload (or null while loading)
//   • mood                — e.g. "ruthless" | "tactical" | …
//   • utterance           — the current ≤5-word line
//   • flickerTrigger      — monotonically-increasing counter that bumps
//                           every time `last_sync_at` changes; child
//                           components (TwinPoster) animate on bump.
//
// Lightweight: one fetch per minute, one shared payload across the app.

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api from "@/api";


const TwinContext = createContext({
    twin: null,
    mood: "tactical",
    utterance: "",
    flickerTrigger: 0,
    refresh: () => {},
});


export function TwinProvider({ children }) {
    const [twin, setTwin] = useState(null);
    const [flickerTrigger, setFlickerTrigger] = useState(0);
    const lastSyncRef = useRef(null);
    const hadOnceRef = useRef(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/twin/current");
            const next = r.data || null;
            setTwin(next);
            // Bump the flicker trigger only when a NEW sync timestamp
            // arrives — never on the very first load (we don't want a
            // hologram-flicker to fire just because the page mounted).
            const ts = next?.last_sync_at || null;
            if (hadOnceRef.current && ts && ts !== lastSyncRef.current) {
                setFlickerTrigger((n) => n + 1);
            }
            lastSyncRef.current = ts;
            hadOnceRef.current = true;
        } catch {
            /* swallow — auth handler will redirect when needed */
        }
    }, []);

    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 60_000);
        const onFocus = () => refresh();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(t);
            window.removeEventListener("focus", onFocus);
        };
    }, [refresh]);

    return (
        <TwinContext.Provider value={{
            twin,
            mood:      twin?.mood || "tactical",
            utterance: twin?.utterance || "",
            flickerTrigger,
            refresh,
        }}>
            {children}
        </TwinContext.Provider>
    );
}


export function useTwin() {
    return useContext(TwinContext);
}
